import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { updatePrintingForm } from "../api";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/Printing/printingSlice";
import { printing } from "../../../../store/queries/Printing";

const useShowForm = () => {
  const dispatch = useDispatch();
  const { formData, selectedListId, activeId, data } = useSelector((state) => state.printing);
  // Track version-specific form values
  const [versionValues, setVersionValues] = useState({});
  const [initialFormStructure, setInitialFormStructure] = useState({});

  // Create the initial form structure
  useEffect(() => {
    if (formData?.length !== 0) {
      let structure = {};
      
      // Build initial structure
      for (const field of formData?.qc_fields || []) {
        const slug = field?.slug;
        structure[slug] = field?.value || "";
      }
      
      // Check if cstone_sstone is 1 before adding center_stone_fields and side_stone_fields
      if (formData?.cstone_sstone === 1) {
        for (const field of formData?.center_stone_fields || []) {
          const slug = field?.slug;
          structure[`center_stone_${slug}`] = field?.value || "";
        }

        for (const field of formData?.side_stone_fields || []) {
          const slug = field?.slug;
          structure[`side_stone_${slug}`] = field?.value || "";
        }
      }
      
      setInitialFormStructure(structure);
    }
  }, [formData]);

  // Initialize formik with dynamic structure
  const formik = useFormik({
    initialValues: initialFormStructure,
    enableReinitialize: true, // Add this to allow initial values to be updated
    onSubmit: async (values) => {
      let data = {
        item_detail_id: selectedListId,
        version_id: activeId, // Include the version ID
        ...values,
      };

      let formDatas = new FormData();
      for (let key in data) {
        if (key.startsWith("center_stone")) {
          const formattedKey = key.replace("center_stone_", "");
          formDatas.append(`center_stone[${formattedKey}]`, data[key]);
        } else if (key.startsWith("side_stone")) {
          const formattedKey = key.replace("side_stone_", "");
          formDatas.append(`side_stone[${formattedKey}]`, data[key]);
        } else {
          formDatas.append(key, data[key]);
        }
      }
      
      const response = await updatePrintingForm(formDatas);
      try {
        if (response?.data?.status_code === 200) {
          dispatch(printing.util.invalidateTags(["reissue"]));
          toast.success(response?.data?.message);
          
          // Save the updated values for this version
          setVersionValues(prev => ({
            ...prev,
            [activeId]: { ...formik.values }
          }));
          
          dispatch(
            updateConfig((state) => {
              state.showForm = false;
              state.isEdit = false;
            })
          );
        } else if (response?.status_code === 422) {
          let errors = response?.errors;
          let errorFields = Object.keys(errors);
          errorFields.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
        } else {
          toast.error(`Something went wrong: Please try again`);
        }
      } catch (error) {
        console.error("Error:", error);
      }
    },
  });

  // Initialize version values from formData
  useEffect(() => {
    if (formData) {
      // Initialize values for all available versions
      const initialVersionValues = {};
      
      // First, set default values for Version 1
      const version1Values = { ...initialFormStructure };
      
      // Process fields for Version 1 - use either updated_values or default field values
      if (formData.updated_values && formData.updated_values[0]) {
        formData.updated_values[0].data?.forEach(field => {
          version1Values[field.slug] = field.value || "";
        });
      } else {
        // If no updated values, use initial values from fields
        formData?.qc_fields?.forEach((field) => {
          version1Values[field?.slug] = field?.value || "";
        });
        
        if (formData?.cstone_sstone === 1) {
          formData?.center_stone_fields?.forEach((field) => {
            version1Values[`center_stone_${field?.slug}`] = field?.value || "";
          });
          
          formData?.side_stone_fields?.forEach((field) => {
            version1Values[`side_stone_${field?.slug}`] = field?.value || "";
          });
        }
      }
      
      initialVersionValues[1] = version1Values;
      
      // Process existing versions from updated_values for versions 2+
      if (formData.updated_values) {
        formData.updated_values.forEach((versionData, index) => {
          // Skip index 0 as we've already handled Version 1
          if (index > 0) {
            const versionId = index + 1;
            const values = { ...initialFormStructure };
            
            // Process fields for this version
            versionData.data?.forEach(field => {
              values[field.slug] = field.value || "";
            });
            
            // Additional processing for center/side stones if needed
            // This would need adjustment based on your API structure
            
            initialVersionValues[versionId] = values;
          }
        });
      }
      
      // Set the version values state
      setVersionValues(initialVersionValues);
      
      // Load values for current activeId
      if (initialVersionValues[activeId]) {
        const currentVersionValues = initialVersionValues[activeId];
        Object.keys(currentVersionValues).forEach(key => {
          formik.setFieldValue(key, currentVersionValues[key]);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, initialFormStructure]);

  // Update form values when activeId changes
  useEffect(() => {
    // Save current form values before switching
    if (Object.keys(formik.values).length > 0) {
      setVersionValues(prev => ({
        ...prev,
        [activeId]: { ...formik.values }
      }));
    }
    
    // Load values for the new active version
    if (versionValues[activeId]) {
      const versionFormValues = versionValues[activeId];
      Object.keys(versionFormValues).forEach(key => {
        if (key in formik.values) {
          formik.setFieldValue(key, versionFormValues[key]);
        }
      });
} else {
  // For new versions, initialize with values from the last version
  const lastVersionId = Math.max(...Object.keys(versionValues).map(Number));
  const baseValues = versionValues[lastVersionId] || initialFormStructure;
  Object.keys(formik.values).forEach(key => {
    formik.setFieldValue(key, baseValues[key] || "");
  });

  // Save these values for the new version
  setVersionValues(prev => ({
    ...prev,
    [activeId]: { ...formik.values }
  }));
}
  }, [activeId]);

  const getFieldError = (fieldName) => {
    if (formik?.touched?.[fieldName] && formik?.errors?.[fieldName]) {
      return formik?.errors?.[fieldName];
    }
    return "";
  };

  const handleAdd = () => {
    // Save current values before creating a new version
    setVersionValues(prev => ({
      ...prev,
      [activeId]: { ...formik.values }
    }));
    
    dispatch(
      updateConfig((state) => {
        const currentVersions = state.data.map((item) => item.name);
        let versionNumber = 1;
        while (currentVersions.includes(`Version ${versionNumber}`)) {
          versionNumber++;
        }
        const newVersionName = `Version ${versionNumber}`;

        // Disable all existing versions
        state.data.forEach((item) => {
          item.disabled = true;
        });

        // Add the new version and enable it
        state.data.push({ name: newVersionName, disabled: false });
        state.activeId = state.data.length; // Set the activeId to the index of the newly added version.
      })
    );
  };

  const handleClick = (item, index) => {
    // Save current form values before switching tabs
    setVersionValues(prev => ({
      ...prev,
      [activeId]: { ...formik.values }
    }));
    
    // Switch to new tab
    dispatch(updateConfig((state) => (state.activeId = index + 1)));
  };

  return { 
    formik, 
    formData, 
    getFieldError, 
    data, 
    activeId, 
    handleAdd, 
    handleClick 
  };
};

export default useShowForm;